import * as Sentry from '@sentry/react'
import { getDefaultFormState, type RJSFSchema, type RJSFValidationError } from '@rjsf/utils'
import { type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import { requiredRolesFor, roleSlotLabel } from './accountTypeCatalog'
import { partyKey } from './parties'
import { isRecord, titleCase, withoutBlanksFor, type SchemaNode } from './schemaValues'
import { isBranchNoise, schemaErrorMessage } from './schemaErrorText'
import { WIZARD_DEFAULTS_BEHAVIOR, wizardValidator } from '../rjsf/wizardValidator'
import { type WizardAccount, type WizardFormContext } from './types'

export interface MissingItem {
  key: string
  label: string
  fieldId?: string
}

const isIndex = (segment: string): boolean => /^\d+$/.test(segment)

const childSchema = (node: SchemaNode | null, property: string): SchemaNode | null => {
  if (isIndex(property)) {
    const items: unknown = node?.items
    const held: unknown = Array.isArray(items) ? items[Number(property)] : items
    return isRecord(held) ? held : null
  }
  const properties: unknown = node?.properties
  if (!isRecord(properties)) {
    return null
  }
  const child: unknown = properties[property]
  return isRecord(child) ? child : null
}

const titleOf = (node: SchemaNode | null): string | null => (typeof node?.title === 'string' && node.title.length > 0 ? node.title : null)

const schemaFieldLabel = (schema: SchemaNode, propertyPath: string, missingProperty: string): string => {
  const segments = propertyPath.split('.').filter(Boolean)
  if (segments.length === 0) {
    return titleCase(missingProperty)
  }
  const trail = segments.reduce<(SchemaNode | null)[]>(
    (nodes, segment) => [...nodes, childSchema(nodes[nodes.length - 1], segment)],
    [schema as SchemaNode | null],
  )
  const own = titleOf(trail[trail.length - 1])
  if (own) {
    return own
  }
  if (isIndex(segments[segments.length - 1])) {
    const named = trail.slice(1, -1).reverse().map(titleOf).find(Boolean)
    if (named) {
      return named
    }
  }
  return titleCase([...segments].reverse().find((segment) => !isIndex(segment)) ?? segments[segments.length - 1])
}

const rjsfFieldId = (propertyPath: string): string => ['root', ...propertyPath.split('.').filter(Boolean)].join('_')

const asRendered = (schema: Record<string, unknown>, values: Record<string, unknown>): Record<string, unknown> =>
  (getDefaultFormState(wizardValidator, schema as RJSFSchema, values, schema as RJSFSchema, undefined, WIZARD_DEFAULTS_BEHAVIOR) as
    | Record<string, unknown>
    | undefined) ?? values

interface ReportedError {
  item: MissingItem
  from?: string
  path: string
  schemaPath: string
}

type Describe = (label: string, error: RJSFValidationError, schema: SchemaNode) => { label: string; from?: string }

const restates = (error: ReportedError, other: ReportedError): boolean =>
  error.from !== undefined && error.from === other.from && error.schemaPath !== other.schemaPath && error.schemaPath.startsWith(`${other.schemaPath}/`)

const onePerAnnotation = (reported: ReportedError[]): MissingItem[] => {
  const taken = new Set<string>()
  return reported
    .filter((error) => {
      if (error.from === undefined) {
        return true
      }
      if (reported.some((other) => restates(error, other))) {
        return false
      }
      const key = `${error.from}|${error.path}`
      if (taken.has(key)) {
        return false
      }
      taken.add(key)
      return true
    })
    .map(({ item }) => item)
}

const schemaErrors = (
  schema: Record<string, unknown>,
  values: Record<string, unknown>,
  keep: (name: string | undefined) => boolean,
  describe: Describe,
  keyPrefix: string,
): MissingItem[] => {
  if (Object.keys(schema ?? {}).length === 0) {
    return []
  }
  try {
    const reported = wizardValidator
      .validateFormData(withoutBlanksFor(schema, asRendered(schema, values ?? {})), schema as RJSFSchema)
      .errors.filter((error) => keep(error.name) && !isBranchNoise(error))
      .map((error) => {
        const missingProperty = String((error.params as { missingProperty?: string } | undefined)?.missingProperty ?? '')
        const propertyPath = error.property ?? ''
        const described = describe(schemaFieldLabel(schema, propertyPath, missingProperty), error, schema)
        return {
          item: {
            key: `${keyPrefix}:${error.name ?? 'invalid'}:${propertyPath || missingProperty}`,
            label: described.label,
            fieldId: rjsfFieldId(propertyPath || missingProperty),
          },
          from: described.from,
          path: propertyPath,
          schemaPath: error.schemaPath ?? '',
        }
      })
    return onePerAnnotation(reported)
  } catch (error) {
    Sentry.captureException(error)
    return []
  }
}

export const schemaMissingItems = (schema: Record<string, unknown>, values: Record<string, unknown>, keyPrefix: string): MissingItem[] =>
  schemaErrors(
    schema,
    values,
    (name) => name === 'required',
    (label) => ({ label }),
    keyPrefix,
  )

export const schemaInvalidItems = (schema: Record<string, unknown>, values: Record<string, unknown>, keyPrefix: string): MissingItem[] =>
  schemaErrors(
    schema,
    values,
    (name) => name !== 'required',
    (label, error, validated) => {
      const message = schemaErrorMessage(validated, error)
      return { label: [label, message.text].filter(Boolean).join(' '), from: message.from }
    },
    keyPrefix,
  )

export const axisMissingItems = (selectors: AccountOpeningTypeSelector[], account: WizardAccount): MissingItem[] =>
  selectors.filter((selector) => !account.selection[selector.field]).map((selector) => ({ key: selector.field, label: selector.label }))

export const holderMissingItems = (accountTypes: AccountType[], account: WizardAccount): MissingItem[] =>
  requiredRolesFor(accountTypes, account.typeId).flatMap((required) => {
    const named = account.holders.filter((holder) => holder.roleCode === required.role && holder.name.trim().length > 0).length
    return Array.from({ length: Math.max(0, required.min - named) }, (_, offset) => ({
      key: `role:${required.role}:${named + offset}`,
      label: `${roleSlotLabel(required)} name`,
    }))
  })

export const holderConflictItems = (account: WizardAccount): MissingItem[] => {
  const items: MissingItem[] = []
  const firstAt = new Map<string, number>()
  account.holders.forEach((holder, index) => {
    const name = holder.name.trim()
    if (!name) {
      return
    }
    const key = `${partyKey(name)}|${holder.roleCode}`
    const earlier = firstAt.get(key)
    if (earlier === undefined) {
      firstAt.set(key, index)
      return
    }
    items.push({ key: `duplicate:${key}:${index}`, label: `${name} is named twice — holder ${index + 1} repeats holder ${earlier + 1}` })
  })
  return items
}

export const missingFieldContext = (items: MissingItem[]): WizardFormContext => ({
  missingFieldIds: new Set(items.map((item) => item.fieldId).filter((fieldId): fieldId is string => !!fieldId)),
})

export const isIntakeReady = (account: WizardAccount): boolean => !!account.typeId
